/**
 * QuoteCard Component
 * Mobile-first responsive quote card with three layouts:
 * - Mobile: Single column, thumb-friendly
 * - Tablet: Dense cards in grid
 * - Desktop: Returns null (handled by table component)
 */

import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  Checkbox,
} from "@mui/material";
import {
  Message,
  Visibility,
  Email,
  LocationOn,
  Send,
} from "@mui/icons-material";
import {
  formatCurrency,
  formatDateShort,
} from "../../../shared/utils/formatters";
import { getPaymentStatus } from "../utils/paymentStatus";
import useDeviceType from "../../leads/hooks/useDeviceType";
import {
  getQuotePdfStatus,
  getQuoteItemsListPdfStatus,
  getQuotePdfStatusLabel,
  getCombinedPdfStatusLabel,
  getCombinedPdfStatusColor,
} from "../utils/quotePdfReadiness";
import { getQuoteNextAction } from "../services/quoteOperationalStatus.js";

/**
 * Get total cost for a quote
 */
const getTotalCost = (quote) => {
  return quote.totalCost !== undefined
    ? quote.totalCost
    : (quote.estimatedCost || 0) + (quote.addOnsCost || 0);
};

/**
 * Primary CTA label/action. Special-cases proof_received + call requests;
 * otherwise uses Quote Ops next-action helpers.
 */
const getPrimaryAction = (quote) => {
  if (quote?.payments?.paymentStatus === "proof_received") {
    return { label: "Verify Payment", action: "verify" };
  }
  if (quote?.hasPendingCallRequest) {
    return { label: "Respond", action: "respond" };
  }
  const next = getQuoteNextAction(quote);
  if (!next || next.key === "none") {
    return { label: "View Quote", action: "view" };
  }
  return { label: next.label, action: next.key };
};

/**
 * QuoteCard Component
 */
export const QuoteCard = ({
  quote,
  onView,
  onWhatsApp,
  onPrimaryAction,
  onSendEmail,
  onResendEmail,
  onRouteClick,
  selectionMode = false,
  isSelected = false,
  onToggleSelection,
  showPdfStatus = false,
  onGeneratePdf,
  generatingPdf = false,
  onSendQuote,
}) => {
  const { isMobile, isTablet } = useDeviceType();
  const paymentStatus = getPaymentStatus(quote);
  const totalCost = getTotalCost(quote);
  const primaryAction = getPrimaryAction(quote);
  const pdfStatus = getQuotePdfStatus(quote);

  const renderWorkflowExtras = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
      {selectionMode && (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
          onClick={(event) => event.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelection?.(quote.id)}
          />
          <Typography variant="body2">Select to bulk send</Typography>
        </Box>
      )}
      {showPdfStatus && (
        <>
          <Chip
            label={getCombinedPdfStatusLabel(quote)}
            color={getCombinedPdfStatusColor(quote)}
            size="small"
            sx={{ width: "fit-content", fontWeight: 500 }}
          />
          {(pdfStatus !== "ready" ||
            getQuoteItemsListPdfStatus(quote) !== "ready") && (
            <Typography variant="caption" color="text.secondary">
              Summary: {getQuotePdfStatusLabel(pdfStatus)} · Items list:{" "}
              {getQuotePdfStatusLabel(getQuoteItemsListPdfStatus(quote))}
            </Typography>
          )}
          {/* Generate PDF removed — send regenerates PDFs */}
        </>
      )}
      {selectionMode && onSendQuote && (
        <Button
          size="small"
          variant="contained"
          startIcon={<Send />}
          onClick={(event) => {
            event.stopPropagation();
            onSendQuote(quote);
          }}
        >
          Send quote
        </Button>
      )}
    </Box>
  );

  // Helper function to open Google Maps for an address
  const handleAddressClick = (e, address) => {
    e.stopPropagation(); // Prevent card click from firing
    if (!address) return;
    const encodedAddress = encodeURIComponent(address);
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
      "_blank"
    );
  };

  // Helper function to open map drawer for route
  const handleRouteClick = (e) => {
    e.stopPropagation(); // Prevent card click from firing
    if (!quote.from || !quote.to) return;
    if (onRouteClick) {
      onRouteClick(quote, e);
    }
  };

  // Mobile layout: Single column, thumb-friendly
  if (isMobile) {
    const canSendEmail =
      quote.status !== "sent" &&
      quote.status !== "accepted" &&
      quote.status !== "rejected" &&
      quote.customerEmail &&
      onSendEmail;
    const canResendEmail =
      quote.status === "sent" && quote.customerEmail && onResendEmail;

    return (
      <Card
        sx={{
          mb: 2,
          borderRadius: 2,
          boxShadow: 2,
          cursor: "pointer",
          "&:active": {
            transform: "scale(0.98)",
          },
          "&:hover": {
            boxShadow: 3,
          },
        }}
        onClick={onView}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Header: Quote ID + Date */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1.5,
            }}
          >
            <Typography
              variant="caption"
              onClick={(e) => {
                e.stopPropagation();
                onView?.(quote);
              }}
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                color: "primary.main",
                cursor: "pointer",
                fontWeight: 600,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {quote.id}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateShort(quote.createdAt)}
            </Typography>
          </Box>

          {/* Route - Prominent with clickable addresses */}
          <Box mb={1}>
            {quote.from && quote.to && (
              <Box
                onClick={handleRouteClick}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  mb: 0.5,
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              >
                <LocationOn color="primary" fontSize="small" />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  Route
                </Typography>
              </Box>
            )}
            {!quote.from || !quote.to ? (
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 0.5, color: "text.primary" }}
              >
                Route
              </Typography>
            ) : null}
            {quote.from && (
              <Box
                onClick={(e) => handleAddressClick(e, quote.from)}
                sx={{
                  display: "block",
                  cursor: "pointer",
                  color: "text.secondary",
                  mb: 0.5,
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.main",
                  },
                }}
              >
                <Typography variant="body2" component="span">
                  From: {quote.from}
                </Typography>
              </Box>
            )}
            {quote.to && (
              <Box
                onClick={(e) => handleAddressClick(e, quote.to)}
                sx={{
                  display: "block",
                  cursor: "pointer",
                  color: "text.secondary",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.main",
                  },
                }}
              >
                <Typography variant="body2" component="span">
                  To: {quote.to}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Customer + Date */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {quote.customerName || "Unknown"} ·{" "}
            {quote.dates && quote.dates.length > 0
              ? quote.dates[0]
              : quote.date || "No date"}
          </Typography>

          {/* Amount - VISUALLY DOMINANT */}
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onView?.(quote);
            }}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                fontSize: "1.5rem",
              }}
            >
              {totalCost > 0 ? formatCurrency(totalCost) : "Not set"}
            </Typography>
            <Chip
              label={paymentStatus.label}
              color={paymentStatus.color}
              variant={paymentStatus.variant}
              size="small"
              sx={{ fontWeight: 500 }}
            />
          </Box>

          {renderWorkflowExtras()}

          {/* Actions - Thumb-friendly */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Message />}
              onClick={(e) => {
                e.stopPropagation();
                onWhatsApp?.(quote);
              }}
              sx={{ flex: 1, minHeight: 44, minWidth: "calc(50% - 4px)" }}
            >
              WhatsApp
            </Button>

            {/* Combined quote send button */}
            {canSendEmail && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Email />}
                onClick={(e) => {
                  e.stopPropagation();
                  onSendEmail(quote);
                }}
                sx={{ flex: 1, minHeight: 44, minWidth: "calc(50% - 4px)" }}
              >
                Send Email
              </Button>
            )}

            {/* Combined quote resend button */}
            {canResendEmail && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Email />}
                onClick={(e) => {
                  e.stopPropagation();
                  onResendEmail(quote);
                }}
                sx={{ flex: 1, minHeight: 44, minWidth: "calc(50% - 4px)" }}
              >
                Resend Email
              </Button>
            )}

            <Button
              variant="contained"
              size="small"
              startIcon={<Visibility />}
              onClick={(e) => {
                e.stopPropagation();
                if (primaryAction.action === "view") {
                  onView?.(quote);
                } else {
                  onPrimaryAction?.(quote, primaryAction.action);
                }
              }}
              sx={{ flex: 1, minHeight: 44, minWidth: "100%" }}
            >
              {primaryAction.label}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Tablet layout: Dense cards, two-column grid
  if (isTablet) {
    return (
      <Card
        sx={{
          height: "100%",
          borderRadius: 2,
          boxShadow: 1,
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            boxShadow: 3,
            transform: "translateY(-2px)",
          },
        }}
        onClick={onView}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1.5,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                onClick={(e) => {
                  e.stopPropagation();
                  onView?.(quote);
                }}
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.7rem",
                  color: "primary.main",
                  display: "block",
                  mb: 0.5,
                  cursor: "pointer",
                  fontWeight: 600,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {quote.id}
              </Typography>
              <Box mb={0.5}>
                {quote.from && quote.to && (
                  <Box
                    onClick={handleRouteClick}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.25,
                      cursor: "pointer",
                      mb: 0.25,
                      "&:hover": {
                        opacity: 0.8,
                      },
                    }}
                  >
                    <LocationOn
                      color="primary"
                      fontSize="small"
                      sx={{ fontSize: "0.75rem" }}
                    />
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{ fontWeight: 600, color: "primary.main" }}
                    >
                      Route
                    </Typography>
                  </Box>
                )}
                {quote.from && (
                  <Box
                    onClick={(e) => handleAddressClick(e, quote.from)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                      color: "text.secondary",
                      mb: 0.25,
                      "&:hover": {
                        textDecoration: "underline",
                        color: "primary.main",
                      },
                    }}
                  >
                    <Typography variant="caption" component="span">
                      From: {quote.from}
                    </Typography>
                  </Box>
                )}
                {quote.to && (
                  <Box
                    onClick={(e) => handleAddressClick(e, quote.to)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                      color: "text.secondary",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "primary.main",
                      },
                    }}
                  >
                    <Typography variant="caption" component="span">
                      To: {quote.to}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {quote.customerName || "Unknown"}
              </Typography>
            </Box>
            <Chip
              label={paymentStatus.label}
              color={paymentStatus.color}
              variant={paymentStatus.variant}
              size="small"
            />
          </Box>

          {renderWorkflowExtras()}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "primary.main" }}
            >
              {totalCost > 0 ? formatCurrency(totalCost) : "Not set"}
            </Typography>
            {quote?.followUpSummary?.count > 0 && (
              <Chip
                label={`${quote.followUpSummary.count} follow-ups`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Desktop: Returns null (handled by table component)
  return null;
};

export default QuoteCard;
